import { useMemo, useEffect, useState, useRef, useCallback } from "react";
import { withStyles } from "@material-ui/core";
import { QueryClient, QueryClientProvider, useQuery } from "react-query";
import PropTypes from "prop-types";
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useAsyncDebounce,
  useSortBy,
} from "react-table";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import MDBox from "components/MDBox";
import CTLTableBodyCell from "./CTLTableBodyCell";
import CTLTableHeadCell from "./CTLTableHeadCell";
import { Checkbox, TableCell, TablePagination } from "@mui/material";
import TableContainer from "@mui/material/TableContainer";
import {
  useMaterialUIController,
  setTableValue,
  setAction,
  setTableSortColumn,
  setTableColumnDataType,
  setValue,
} from "context";
import CTLLinearProgress from "../CTLLinearProgress";
import { application } from "globals/endpoints";
import CTLNotification from "../CTLNotification";
import MDTypography from "components/MDTypography";
import { setTableColumnDirection } from "context";

const queryClient = new QueryClient();

function CTLTableContent({
  table,
  apiURL,
  isSorted,
  defaultSortColumn,
  defaultSortDirection,
  noEndBorder,
  pageId,
  searchText,
  hasRowSelection,
  advancedFilters,
  onClearFilters,
  updateList,
  onSelectedIDdata,
  onSelectedRowData,
  disablePagination,
  disableSort,
  refetchInterval,
  defaultPageSize,
  footerContent,
}) {
  useEffect(() => {
    setSelectedId([]);
    setSelectAll(false);
    setSelectedRow([]);
  }, [updateList]);

  useEffect(() => {
    setQueryPageIndex(0);
  }, [searchText]);

  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
    pagename: "",
    status: "",
  });
  const [controller, dispatch] = useMaterialUIController();
  const { darkMode, value, action } = controller;
  const [clickedHeader, setClickedHeader] = useState("");
  const [queryPageIndex, setQueryPageIndex] = useState(0);
  const [queryPageCount, setQueryPageCount] = useState(0);
  // Initial estimate for rows per page
  const INITIAL_ROW_HEIGHT = 32;
  const INITIAL_OFFSET = 140;
  const initialPageSize = Math.max(10, Math.floor((window.innerHeight - INITIAL_OFFSET) / INITIAL_ROW_HEIGHT));
  const [dynamicPageSize, setDynamicPageSize] = useState(initialPageSize);
  const [queryPageSize, setQueryPageSize] = useState(initialPageSize);
  const tableContainerRef = useRef(null);
  const hasRecalculated = useRef(false);
  const [querySortColumn, setQuerySortColumn] = useState(defaultSortColumn);
  const [querySortDirection, setQuerySortDirection] =
    useState(defaultSortDirection);
  const [querySortDataType, setQuerySortDataType] = useState("");
  const [selectAll, setSelectAll] = useState(false);
  const [selectedId, setSelectedId] = useState([]);
  const [selectedRow, setSelectedRow] = useState([]);
  const [lastEpoch, setLastEpoch] = useState(0);
  onSelectedIDdata(selectedId);
  onSelectedRowData(selectedRow);
  const StyledTablePagination = withStyles((theme) => ({
    toolbar: {
      fontWeight: "500",
      fontSize: "0.8125rem",
      color: darkMode ? "white" : "rgba(0, 0, 0, 0.87)",
    },
  }))(TablePagination);

  const columns = useMemo(() => table.columns, [table]);

  //const data = useMemo(() => table.rows, [table]);
  const [tableData, setTableData] = useState([]);
  const { isLoading, error, data, isSuccess } = useQuery(
    [
      lastEpoch,
      pageId,
      searchText,
      advancedFilters,
      updateList,
      queryPageIndex,
      queryPageSize,
      querySortColumn,
      querySortDirection,
    ],
    () =>
      fetchData(
        queryPageIndex,
        queryPageSize,
        querySortColumn,
        querySortDirection,
        searchText,
        advancedFilters,
        updateList
      ),
    {
      keepPreviousData: false,
      staleTime: 5000,
      refetchInterval: refetchInterval,
    }
  );

  const fetchData = async (
    newPage,
    newPageSize,
    sortColumn,
    sortDirection,
    searchText,
    advancedFilters
  ) => {
    if (!sortDirection) {
      sortColumn = "";
    }
    setQueryPageCount;
    const pageDefinition = {
      currentPage: newPage,
      pageSize: newPageSize,
      sortDirection: sortDirection ? sortDirection : defaultSortDirection,
      sortBy: sortColumn ? sortColumn : defaultSortColumn,
      search: searchText,
      sortDataType: querySortDataType,
      advancedFilters: advancedFilters,
    };

    if (disablePagination) {
      const response = await application.post(apiURL, pageDefinition);
      setQueryPageCount(1);
      return {
        results: response.data.data,
      };
    } else {
      const response = await application.post(apiURL, pageDefinition);
      setQueryPageCount(response.data.data.totalRecords);
      return {
        results: response.data.data.currentPageData,
      };
    }
  };
  useEffect(() => {
    if (isSuccess) {
      setTableData(data?.results || []);
    } else {
      setTableData([]);
    }
  }, [data, isSuccess]);

  const [filteredData, setFilteredData] = useState([]);

  useEffect(() => {
    if (disablePagination && searchText) {
      const lowercasedSearch = searchText.toLowerCase();
      const filtered =
        tableData &&
        tableData.filter((row) => {
          return Object?.values(row)?.some((value) =>
            String(value)?.toLowerCase()?.includes(lowercasedSearch)
          );
        });
      setFilteredData(filtered);
    } else {
      setFilteredData(tableData);
    }
  }, [disablePagination, searchText, tableData]);

  const tableInstance = useTable(
    {
      columns,
      data: disablePagination ? filteredData : tableData,
      manualPagination: true,
      initialState: { pageSize: defaultPageSize || 10 },
    },
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    setPageSize,
    state: { pageSize },
  } = tableInstance;

  useEffect(() => {
    setPageSize(initialPageSize);
    setQueryPageSize(initialPageSize);
  }, []);

  // After first render, measure actual row height and recalculate
  useEffect(() => {
    if (hasRecalculated.current || !tableContainerRef.current || page.length === 0) return;

    const timer = setTimeout(() => {
      const container = tableContainerRef.current;
      if (!container) return;

      const rows = container.querySelectorAll("tbody tr");
      if (rows.length === 0) return;

      // Measure actual row height from rendered rows
      const actualRowHeight = rows[0].getBoundingClientRect().height;
      if (actualRowHeight <= 0) return;

      // Measure available container height
      const containerHeight = container.getBoundingClientRect().height;
      const thead = container.querySelector("thead");
      const headerHeight = thead ? thead.getBoundingClientRect().height : 40;
      const availableHeight = containerHeight - headerHeight;

      const newPageSize = Math.max(10, Math.floor(availableHeight / actualRowHeight));

      if (newPageSize !== dynamicPageSize) {
        setDynamicPageSize(newPageSize);
        setPageSize(newPageSize);
        setQueryPageSize(newPageSize);
        hasRecalculated.current = true;
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [page.length]);

  const handlePageChange = (event, newPage) => {
    setQueryPageIndex(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPageSize(newPageSize);
    setQueryPageSize(newPageSize);
    setQueryPageIndex(0);
  };

  const handleSortChange = (column, columnDataType) => {
    if (!column.sortDisable) {
      if (!disableSort) {
        const actualColumnId = column.customSortByName || column.id;

        if (querySortColumn === actualColumnId) {
          if (querySortDirection === "asc") {
            setQuerySortDirection("desc");
            setTableColumnDirection(dispatch, "desc");
          } else if (querySortDirection === "desc") {
            setQuerySortDirection("none");
            setTableColumnDirection(dispatch, "none");
          } else {
            setQuerySortDirection("asc");
            setTableColumnDirection(dispatch, "asc");
          }
        } else {
          setQuerySortColumn(actualColumnId);
          setTableSortColumn(dispatch, column.id);
          setQuerySortDirection("asc");
          setTableColumnDirection(dispatch, "asc");
        }

        setQuerySortDataType(columnDataType);
        setTableColumnDataType(dispatch, columnDataType);
        setClickedHeader(column.id);
      }
    } else {
      setNotify({
        isOpen: true,
        status: `Searching & Sorting is not available on ${column.Header}`,
        type: "error",
      });
    }
  };
  const setSortedValue = (column) => {
    let sortedValue;
    let newSortInfo = {
      sortBy: "",
      sortDirection: "",
    };

    if (isSorted && column.isSorted) {
      sortedValue = column.isSortedDesc ? "desc" : "asce";
      newSortInfo.sortDirection = column.isSortedDesc ? "desc" : "asc";
      newSortInfo.sortBy = column.id;
    } else if (isSorted) {
      sortedValue = "none";
    } else {
      sortedValue = false;
    }

    return sortedValue;
  };

  const rowSelection = (rowId) => {
    const idList = [...selectedId];
    const itemIndex = idList.indexOf(rowId);
    if (itemIndex >= 0) {
      idList.splice(itemIndex, 1);
    } else {
      idList.push(rowId);
    }
    setSelectedId(idList);
  };

  const onRowSelection = (row) => {
    const List = [...selectedRow];
    const itemIndex = List.indexOf(row);
    if (itemIndex >= 0) {
      List.splice(itemIndex, 1);
    } else {
      List.push(row);
    }
    setSelectedRow(List);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedId([]);
    } else {
      const allIds = tableData.map((row) => row.id);
      setSelectedId(allIds);
    }
    setSelectAll(!selectAll);
  };

  const handleSelectAllRow = () => {
    if (selectAll) {
      setSelectedRow([]);
    } else {
      const allRow = tableData.map((row) => row);
      setSelectedRow(allRow);
    }
    setSelectAll(!selectAll);
  };

  useEffect(() => {
    if (value !== null && action != "") {
      if (action === "create") {
        setTableData((prevTableData) => [...prevTableData, value]);
      } else if (action === "update") {
        setTableData((prevTableData) => {
          const updatedData = prevTableData.map((item) =>
            item.name === value.name ? value : item
          );
          return updatedData;
        });
      } else if (action === "delete") {
        setTableData((prevTableData) => {
          const filteredData = prevTableData.filter(
            (item) => item.name !== value.name
          );
          return filteredData;
        });
      }
      setAction(dispatch, "");
      setValue(dispatch, null);
    }
  }, [value, action]);

  useEffect(() => {
    setTableValue(dispatch, tableData);
  }, [dispatch, tableData]);

  return (
    <>
      <CTLLinearProgress isLoading={isLoading} />
      <TableContainer ref={tableContainerRef} sx={{ boxShadow: "none", flex: 1, overflow: "auto", borderRadius: 0 }}>
        <Table {...getTableProps()} stickyHeader>
          <MDBox component="thead" sx={{ position: "sticky", top: 0, zIndex: 2 }}>
            {headerGroups.map((headerGroup) => (
              <TableRow {...headerGroup.getHeaderGroupProps()}>
                {hasRowSelection && (
                  <CTLTableHeadCell width="50px">
                    <Checkbox
                      checked={selectAll}
                      onChange={() => {
                        handleSelectAll(), handleSelectAllRow();
                      }}
                    />
                  </CTLTableHeadCell>
                )}
                {headerGroup.headers.map((column) => {
                  if (column.displayInList) {
                    return (
                      <CTLTableHeadCell
                        {...column.getHeaderProps(isSorted)}
                        width={column.width ? column.width : "auto"}
                        align={column.align ? column.align : "left"}
                        sorted={
                          querySortColumn === column.id
                            ? querySortDirection
                            : "none"
                        }
                        onClick={() =>
                          handleSortChange(column, column.dataType)
                        }
                        style={{
                          backgroundColor:
                            clickedHeader === column.id
                              ? darkMode
                                ? "white"
                                : "gray"
                              : "",
                          color:
                            clickedHeader === column.id
                              ? darkMode
                                ? "gray"
                                : "white"
                              : "",
                          padding: clickedHeader === column.id ? "10px" : "",
                          borderRadius:
                            clickedHeader === column.id ? "10px" : "",
                        }}
                      >
                        {column.render("Header")}
                      </CTLTableHeadCell>
                    );
                  }
                })}
              </TableRow>
            ))}
          </MDBox>
          <TableBody {...getTableBodyProps()}>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  style={{
                    textAlign: "center",
                    verticalAlign: "middle",
                    padding: "0",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    <MDTypography>Loading...</MDTypography>
                  </div>
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  style={{
                    textAlign: "center",
                    verticalAlign: "middle",
                    padding: "0",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    <MDTypography>Something went wrong</MDTypography>
                  </div>
                </TableCell>
              </TableRow>
            ) : tableData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  style={{
                    textAlign: "center",
                    verticalAlign: "middle",
                    padding: "0",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    <MDTypography>There are no records to display</MDTypography>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              page.map((row, key) => {
                prepareRow(row);
                return (
                  <TableRow {...row.getRowProps()}>
                    {hasRowSelection && (
                      <CTLTableBodyCell>
                        <Checkbox
                          checked={
                            (row.original.id &&
                              selectedId.includes(row.original.id)) ||
                            selectedRow.includes(row.original)
                          }
                          onChange={() => {
                            rowSelection(row.original.id),
                              onRowSelection(row.original);
                          }}
                        />
                      </CTLTableBodyCell>
                    )}
                    {row.cells.map((cell) => {
                      if (cell.column.displayInList) {
                        return (
                          <CTLTableBodyCell
                            noBorder={noEndBorder && rows.length - 1 === key}
                            align={
                              cell.column.align ? cell.column.align : "left"
                            }
                            {...cell.getCellProps()}
                          >
                            {cell.render("Cell")}
                          </CTLTableBodyCell>
                        );
                      }
                    })}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
          backgroundColor: darkMode ? "#252525" : "#f8f9fa",
          minHeight: 36,
        }}
      >
        <div style={{ flex: 1, overflow: "hidden", display: "flex", alignItems: "center" }}>
        {footerContent && (
          <div style={{ display: "flex", alignItems: "center", paddingLeft: 8, flexShrink: 0 }}>
            {footerContent}
          </div>
        )}
        {advancedFilters?.length > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flex: 1,
              overflow: "hidden",
              fontSize: "0.75rem",
              color: darkMode ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)",
              paddingLeft: 8,
            }}
          >
            <span
              onClick={() => {
                window.dispatchEvent(new CustomEvent("clearAdvancedFilters"));
              }}
              title="Remove Filter"
              style={{
                cursor: "pointer",
                color: darkMode ? "#f44336" : "#d32f2f",
                fontSize: "16px",
                lineHeight: 1,
                marginRight: 6,
                flexShrink: 0,
                fontWeight: "bold",
              }}
            >
              &#x2715;
            </span>
            <span style={{ fontWeight: 600, marginRight: 6, flexShrink: 0 }}>Filter:</span>
            <span style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
              {advancedFilters.map((f, i) => {
                const col = columns.find(c => c.accessor === f.fieldName);
                const name = col ? col.Header : f.fieldName;
                return (
                  <span key={i} style={{ marginRight: 6 }}>
                    {f.direction} {name} {f.value}{i < advancedFilters.length - 1 ? "," : ""}
                  </span>
                );
              })}
            </span>
          </div>
        )}
        </div>
        {!disablePagination && (
          <StyledTablePagination
            showFirstButton={true}
            showLastButton={true}
            component="div"
            page={queryPageIndex}
            count={queryPageCount}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[
              dynamicPageSize,
              ...[25, 50, 100].filter(n => n !== dynamicPageSize),
              { value: queryPageCount || 1000, label: "All" },
            ]}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              flexShrink: 0,
              border: "none",
              color: darkMode ? "#ffffff" : "rgba(0,0,0,0.87)",
              "& .MuiToolbar-root": {
                minHeight: "36px !important",
                paddingTop: 0,
                paddingBottom: 0,
              },
              "& .MuiIconButton-root": {
                color: darkMode ? "#ffffff" : "rgba(0,0,0,0.87)",
              },
              "& .MuiIconButton-root.Mui-disabled": {
                color: darkMode ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.26)",
              },
              "& .MuiSelect-icon": {
                color: darkMode ? "#ffffff" : "rgba(0,0,0,0.54)",
              },
            }}
          />
        )}
      </div>
      <CTLNotification notify={notify} setNotify={setNotify} />
    </>
  );
}

CTLTableContent.defaultProps = {
  entriesPerPage: { defaultValue: 25, entries: [10, 25, 50, 100] },
  canSearch: false,
  showTotalEntries: true,
  pagination: { variant: "gradient", color: "info" },
  isSorted: true,
  noEndBorder: false,
};

CTLTableContent.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
};

const TableWrapper = ({
  table,
  apiURL,
  defaultSortColumn,
  defaultSortDirection,
  pageId,
  searchText,
  hasRowSelection,
  advancedFilters,
  onClearFilters,
  updateList,
  onSelectedIDdata,
  onSelectedRowData,
  disablePagination,
  disableSort,
  refetchInterval,
  defaultPageSize,
  footerContent,
}) => {
  return (
    <>
      <QueryClientProvider client={queryClient}>
        <CTLTableContent
          onSelectedIDdata={onSelectedIDdata}
          onSelectedRowData={onSelectedRowData}
          table={table}
          apiURL={apiURL}
          defaultSortColumn={defaultSortColumn}
          defaultSortDirection={defaultSortDirection}
          pageId={pageId}
          searchText={searchText}
          hasRowSelection={hasRowSelection}
          advancedFilters={advancedFilters}
          onClearFilters={onClearFilters}
          updateList={updateList}
          disablePagination={disablePagination}
          disableSort={disableSort}
          refetchInterval={refetchInterval == null ? 0 : refetchInterval}
          defaultPageSize={defaultPageSize}
          footerContent={footerContent}
        />
      </QueryClientProvider>
    </>
  );
};

export default TableWrapper;
